import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import mqtt from "mqtt";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChooseMachineAlert } from "@/components/login/choose-machine-alert";
import { ConfirmPrivacyAlert } from "@/components/login/confirm-privacy-alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { LoginResponse } from "@/data-interfaces/authentication";
import {
	type ApiError,
	GENERIC_ERROR_CODE,
	isApiError,
} from "@/data-interfaces/generics";
import { ENV_CONFIGURATIONS } from "@/global-configurations";
import {
	doPostRequest,
	LOGIN_QUERY_KEY,
	MQTT_CLIENT_KEY,
} from "@/lib/http-requests";
import { LOGIN_PAGE_URL } from "@/lib/page-routes";
import { LOGIN_URL } from "@/lib/request-paths";
import {
	SSK_CLIENT_ID,
	SSK_JWT_TOKEN,
	SSK_PASSWORD,
	SSK_REFRESH_SESSION_INTERVAL_SEC,
	SSK_SESSION_ID,
	SSK_USERNAME,
} from "@/lib/session-storage-keys";
import {
	getDyamachPrivacyPolicyUrl,
	getSoftwareVersion,
} from "@/lib/string-utils";
import dyamachLogo from "@/resources/dyamach-logo-1.png";

export const Route = createFileRoute(LOGIN_PAGE_URL)({
	component: Login,
});

function Login() {
	const [t, i18n] = useTranslation();
	const [te, __] = useTranslation("apiErrors");

	const queryClient = useQueryClient();

	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [isChooseMachineAlertVisible, setIsChooseMachineAlertVisible] =
		useState(false);
	const [isConfirmPrivacyAlertVisible, setIsConfirmPrivacyAlertVisible] =
		useState(false);

	const getSHA256Hash = async (text: string): Promise<string> => {
		const encoder = new TextEncoder();
		const data = encoder.encode(text);
		const hashBuffer = await crypto.subtle.digest("SHA-256", data);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
	};

	const doLoginRequest = async (): Promise<LoginResponse> => {
		const loginRes = await doPostRequest<LoginResponse>(LOGIN_URL, {
			username: username,
			password: password,
		});

		const clientId = await getSHA256Hash(loginRes.session.access_token);
		sessionStorage.setItem(SSK_USERNAME, username);
		sessionStorage.setItem(SSK_PASSWORD, password);
		sessionStorage.setItem(SSK_JWT_TOKEN, loginRes.session.access_token);
		sessionStorage.setItem(
			SSK_REFRESH_SESSION_INTERVAL_SEC,
			loginRes.session.refresh_after_seconds.toString(),
		);
		sessionStorage.setItem(SSK_CLIENT_ID, clientId);
		sessionStorage.setItem(SSK_SESSION_ID, loginRes.session.id.toString());

		return loginRes;
	};

	const login = useQuery<LoginResponse, AxiosError<ApiError>>({
		queryKey: [LOGIN_QUERY_KEY],
		queryFn: () => doLoginRequest(),
		initialData: undefined,
		enabled: false,
		retry: false,
	});

	const isLatestSoftwareVersion = (
		loginData: LoginResponse | undefined,
	): boolean => {
		if (loginData) {
			return (
				loginData.last_software_versions.dyamach_cloud_gui ===
				getSoftwareVersion()
			);
		}

		return true;
	};

	const connectToMqttBroker = async (
		loginData: LoginResponse,
	): Promise<mqtt.MqttClient> => {
		const BROKER_URL = `${ENV_CONFIGURATIONS.mqttProtocol}://${ENV_CONFIGURATIONS.mqttBrokerHost}:${ENV_CONFIGURATIONS.mqttBrokerPort}/mqtt`;

		return await mqtt.connectAsync(
			BROKER_URL,
			{
				clientId: sessionStorage.getItem(SSK_CLIENT_ID) ?? undefined,
				username: username,
				password: `${loginData.session.token_type} ${loginData.session.access_token}`,
				protocolVersion: 5,
				clean: true,
				reconnectPeriod: 0,
			},
			false,
		);
	};

	const client = useQuery<mqtt.MqttClient>({
		queryKey: [MQTT_CLIENT_KEY],
		queryFn: () => new Promise(() => {}),
		initialData: undefined,
		enabled: false,
	});

	const doLogin = async () => {
		await queryClient.resetQueries({ queryKey: [LOGIN_QUERY_KEY] });

		const loginRes = await login.refetch();

		if (
			loginRes.isError ||
			!loginRes.data ||
			!isLatestSoftwareVersion(loginRes.data)
		) {
			return;
		}

		if (!loginRes.data.user.privacy_accepted) {
			setIsConfirmPrivacyAlertVisible(true);
			return;
		} else {
			setIsConfirmPrivacyAlertVisible(false);
		}

		const clientRes = await queryClient.fetchQuery({
			queryKey: [MQTT_CLIENT_KEY],
			queryFn: () => connectToMqttBroker(loginRes.data),
		});

		if (clientRes.connected) {
			setIsChooseMachineAlertVisible(true);
		}
	};

	return (
		<div className="min-h-screen text-white flex items-center justify-center bg-[radial-gradient(circle_at_50%_50%,#0A1C1C_32%,#308282_100%)]">
			<div className="w-125 h-150">
				<div className="flex flex-col px-3.75 gap-y-6">
					<div className="flex flex-col items-center gap-y-4">
						<img src={dyamachLogo} className="w-25 h-25" alt={"Dyamach logo"} />
						<div>DYAMACH CLOUD</div>
						<div>v. {getSoftwareVersion()}</div>

						{!isLatestSoftwareVersion(login.data) && (
							<div className="text-red-400 text-xl">
								{`${t(
									"The current version of the software is not the latest available.",
								)} ${t("Try returning to login page in incognito browser mode")}`}
							</div>
						)}
					</div>

					{login.isFetching || client.isFetching ? (
						<div className="flex flex-col items-center">
							<Spinner className="size-16 text-[#2B9597]" />
						</div>
					) : (
						<>
							<div className="flex flex-col gap-y-4">
								{login.isError &&
									isApiError(login.error.response?.data) &&
									login.error.response?.data.code !== GENERIC_ERROR_CODE && (
										<div className="text-red-400 text-2xl">
											{te(login.error.response?.data.error)}
										</div>
									)}

								{login.isError &&
									(!isApiError(login.error.response?.data) ||
										login.error.response?.data.code === GENERIC_ERROR_CODE) && (
										<div className="text-red-400 text-2xl">
											{te("Error during login")}
										</div>
									)}

								{client.isError && (
									<div className="text-red-400 text-2xl">
										{te("Error during MQTT broker connection")}
									</div>
								)}

								<div className="flex flex-col gap-y-2">
									<div>{t("Username")}</div>
									<Input
										type="text"
										value={username}
										onChange={(e) => {
											setUsername(e.target.value);
										}}
									/>
								</div>
								<div className="flex flex-col gap-y-2">
									<div>{t("Password")}</div>
									<Input
										type="password"
										value={password}
										onChange={(e) => {
											setPassword(e.target.value);
										}}
									/>
								</div>
							</div>

							<Button
								className="bg-[#2BABAD] hover:bg-[#238b8c]"
								onClick={doLogin}
							>
								{t("Login")}
							</Button>

							<div className="flex justify-center text-[15px] text-[#737373]">
								{t("By clicking login, you agree to")}
								&nbsp;
								<a
									href={getDyamachPrivacyPolicyUrl(i18n.language)}
									target="_blank"
									rel="noopener noreferrer"
									className="underline"
								>
									Dyamach Privacy Policy
								</a>
							</div>
						</>
					)}
				</div>
			</div>

			<ConfirmPrivacyAlert
				isAlertVisibile={isConfirmPrivacyAlertVisible}
				doLogin={doLogin}
			/>

			<ChooseMachineAlert isAlertVisibile={isChooseMachineAlertVisible} />
		</div>
	);
}
